/**
 * @fileoverview VariantPicker
 *
 * Per-document variant-option picker used in:
 *   1. The generate-precedents wizard's Review Fields step
 *   2. The day-one document editor
 *
 * Renders one row per variant group referenced by the template's HTML, with
 * radio-style chips for each option. Defaults to the job-wide selection so
 * the user only sees overrides when they intentionally change one.
 */

import { useCallback, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { ChevronRight } from "lucide-react";
import { cn } from "@/utils/misc";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  extractVariantGroupSlugs,
  type VariantGroup,
} from "./VariantPicker.helpers";

export interface VariantPickerProps {
  /** Variant groups defined org-wide. */
  groups: VariantGroup[];
  /** HTML body of the template — used to detect which groups are actually
      referenced (so we don't render pickers for unused groups). */
  templateHtml?: string | null;
  /** Job-wide defaults (`jobs.variantSelections`). */
  jobSelections?: Record<string, string>;
  /** Per-document overrides (`jobPrecedents.variantSelections`). */
  docSelections?: Record<string, string>;
  /** Called when the user picks an option for a group. */
  onChange: (groupSlug: string, optionValue: string) => void;
  /** Called when the user resets a group override back to the job default. */
  onClear?: (groupSlug: string) => void;
  /** Optional dense layout for the day-one document header. */
  dense?: boolean;
  /**
   * Suppress the "references a variant group that no longer exists"
   * banner. The day-one header renders the picker inline where a
   * full-width banner would break the layout — it surfaces the warning
   * separately via `getOrphanVariantSlugs` instead.
   */
  hideOrphanWarning?: boolean;
  /** All snippets for this org. Used to show the snippet picker when
      multiple snippets exist for the selected option. */
  snippets?: Array<{
    _id: string;
    name: string;
    groupSlug: string;
    optionValue: string;
  }>;
  /** Current snippet selections (group slug → snippet _id). */
  snippetSelections?: Record<string, string>;
  /** Called when the user picks a snippet for a group. */
  onSnippetChange?: (groupSlug: string, snippetId: string) => void;

  /**
   * Per-block preview rows (day-one only). When provided, the group-level
   * snippet dropdown is replaced by a per-block preview list: one row per
   * variant block in the document, each showing the resolved sentence for
   * the selected option with an optional per-block override. Other callers
   * omit this and keep the legacy group-level dropdown.
   */
  blocks?: Array<{
    vbId: string;
    groupSlug: string;
    /** Resolved slot label (snippet name) for the selected option. */
    slotLabel?: string;
    /** Sanitized HTML preview of the resolved sentence for the option. */
    previewHtml?: string;
  }>;
  /** Snippets selectable as a per-block override (caller filters to the
      block's group + currently-selected option). */
  blockOverrideSnippets?: (block: {
    vbId: string;
    groupSlug: string;
  }) => Array<{ _id: string; name: string }>;
  /** Current per-block overrides (vbId → snippet _id). */
  blockSnippetSelections?: Record<string, string>;
  /** Called when a per-block override is chosen (keyed by vbId). */
  onBlockSnippetChange?: (vbId: string, snippetId: string) => void;
  /** Called to clear a per-block override (keyed by vbId). */
  onBlockSnippetClear?: (vbId: string) => void;
  /**
   * When set, groups with a `fieldBinding` render an "Auto" chip instead of
   * "None": with no explicit per-doc pick the option is resolved per
   * document from the bound field at generation time. The wizard passes
   * this; the day-one editor omits it (selections there are already
   * stamped, so a cleared override falls back to the job default).
   */
  bindingAware?: boolean;
}

export function VariantPicker({
  groups,
  templateHtml,
  jobSelections,
  docSelections,
  onChange,
  onClear,
  dense,
  hideOrphanWarning,
  snippets,
  snippetSelections,
  onSnippetChange,
  blocks,
  blockOverrideSnippets,
  blockSnippetSelections,
  onBlockSnippetChange,
  onBlockSnippetClear,
  bindingAware,
}: VariantPickerProps) {
  // Per-block previews are collapsed by default — they're tall and the option
  // chips above are the primary control. Expanded set is keyed by group slug.
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const toggleGroup = useCallback((slug: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  }, []);

  const referencedSlugs = useMemo(
    () => extractVariantGroupSlugs(templateHtml),
    [templateHtml],
  );

  const referencedGroups = useMemo(
    () => groups.filter((g) => referencedSlugs.has(g.slug)),
    [groups, referencedSlugs],
  );

  // Slugs the template uses that aren't in the variant-group list (deleted
  // group, or query hadn't loaded yet). We surface them as a stub row so
  // the user can see WHY they're missing options instead of a silent gap.
  const orphanSlugs = useMemo(() => {
    const knownSlugs = new Set(groups.map((g) => g.slug));
    return [...referencedSlugs].filter((s) => !knownSlugs.has(s));
  }, [referencedSlugs, groups]);

  const resolveValue = useCallback(
    (slug: string): string | null => {
      if (docSelections && slug in docSelections) return docSelections[slug];
      if (jobSelections && slug in jobSelections) return jobSelections[slug];
      return null;
    },
    [jobSelections, docSelections],
  );

  if (
    referencedSlugs.size > 0 &&
    referencedGroups.length === 0 &&
    groups.length === 0
  ) {
    // Variant groups query hasn't returned yet — render a thin loading hint
    // so the slot is visible.
    return (
      <div
        className={cn("text-xs italic text-primary/40", dense ? "" : "py-1")}
      >
        Loading variant options…
      </div>
    );
  }

  const showOrphanWarning = !hideOrphanWarning && orphanSlugs.length > 0;
  if (referencedGroups.length === 0 && !showOrphanWarning) return null;

  return (
    <div className={cn("flex flex-col gap-2", dense ? "text-xs" : "text-sm")}>
      {showOrphanWarning && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-400">
          This template references variant{" "}
          {orphanSlugs.length === 1 ? "group" : "groups"}{" "}
          {orphanSlugs.map((s) => `"${s}"`).join(", ")} which no longer exist in
          this org. Recreate the group to expose options here.
        </div>
      )}
      {referencedGroups.map((group) => {
        // Data-bound groups (in binding-aware mode) resolve their option per
        // document at generation time — only an explicit per-doc pick shows
        // as active; the job-wide default is not effective for them.
        const isBound = bindingAware && !!group.fieldBinding;
        const hasDocSelection = !!docSelections && group.slug in docSelections;
        const active = isBound
          ? hasDocSelection
            ? docSelections[group.slug]
            : null
          : resolveValue(group.slug);
        // The job-wide default for this group, resolved to its option label so
        // the override notice can name what's being overridden. `null` when the
        // job has no default selected for the group.
        const jobDefaultValue = jobSelections?.[group.slug];
        const jobDefaultLabel = jobDefaultValue
          ? (group.options.find((o) => o.value === jobDefaultValue)?.label ??
            jobDefaultValue)
          : null;
        const docOverride = isBound
          ? hasDocSelection
          : docSelections &&
            group.slug in docSelections &&
            (!jobSelections ||
              docSelections[group.slug] !== jobSelections[group.slug]);
        const matchingSnippets = active
          ? (snippets ?? []).filter(
              (s) => s.groupSlug === group.slug && s.optionValue === active,
            )
          : [];
        // When `blocks` is provided (day-one), the per-block preview list
        // below replaces the legacy group-level snippet dropdown.
        const showSnippetPicker =
          !blocks && matchingSnippets.length > 0 && onSnippetChange;
        const selectedSnippetId = snippetSelections?.[group.slug] ?? "";
        const groupBlocks = blocks
          ? blocks.filter((b) => b.groupSlug === group.slug)
          : [];
        return (
          <div
            key={group.slug}
            className={cn("flex flex-col gap-1", dense ? "" : "py-1")}
          >
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={cn(
                  "font-medium text-primary/70",
                  dense ? "text-[11px]" : "text-xs",
                )}
              >
                <span className="mr-1.5 inline-flex items-center rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-600 dark:bg-indigo-950/30 dark:text-indigo-400">
                  Variant
                </span>
                {group.name}
              </span>
              {groupBlocks.length > 0 && (
                <button
                  type="button"
                  onClick={() => toggleGroup(group.slug)}
                  className="inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] font-medium text-primary/50 transition hover:bg-secondary hover:text-primary/80"
                  aria-expanded={expandedGroups.has(group.slug)}
                >
                  <ChevronRight
                    className={cn(
                      "size-3 transition-transform",
                      expandedGroups.has(group.slug) && "rotate-90",
                    )}
                  />
                  {expandedGroups.has(group.slug)
                    ? "Hide"
                    : `${groupBlocks.length} variant${groupBlocks.length === 1 ? "" : "s"}`}
                </button>
              )}
              <div className="flex flex-wrap items-center gap-1">
                {/* Explicit "None" chip — highlighted when nothing resolves
                    (no per-doc override and no job default), so the unselected
                    state is visible rather than a blank row. Clicking it clears
                    any per-doc override back to the job default. For data-bound
                    groups this chip reads "Auto" instead: no override means the
                    option is resolved per document from the bound field. */}
                <button
                  key="__none__"
                  type="button"
                  title={
                    isBound && group.fieldBinding
                      ? `Selected per document from the "${group.fieldBinding.fieldKey}" field value`
                      : undefined
                  }
                  onClick={() => {
                    if (active !== null) onClear?.(group.slug);
                  }}
                  className={cn(
                    "rounded-full border px-2.5 py-0.5 transition",
                    dense ? "text-[11px]" : "text-xs",
                    active === null
                      ? "border-indigo-500 bg-indigo-500 text-white"
                      : "border-border bg-background text-primary/70 hover:border-indigo-300 hover:text-primary",
                  )}
                >
                  {isBound ? "Auto" : "None"}
                </button>
                {group.options.map((opt) => {
                  const isActive = active === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        if (isActive && docOverride && onClear) {
                          onClear(group.slug);
                        } else {
                          onChange(group.slug, opt.value);
                        }
                      }}
                      className={cn(
                        "rounded-full border px-2.5 py-0.5 transition",
                        dense ? "text-[11px]" : "text-xs",
                        isActive
                          ? "border-indigo-500 bg-indigo-500 text-white"
                          : "border-border bg-background text-primary/70 hover:border-indigo-300 hover:text-primary",
                      )}
                    >
                      {opt.label}
                    </button>
                  );
                })}
                {showSnippetPicker && (
                  <Select
                    value={selectedSnippetId || undefined}
                    onValueChange={(val) => onSnippetChange(group.slug, val)}
                  >
                    <SelectTrigger
                      className={cn(
                        "h-auto w-auto gap-1.5 rounded border border-border bg-background px-2 py-0.5 text-primary/70 transition hover:border-indigo-300",
                        dense ? "text-[11px]" : "text-xs",
                      )}
                    >
                      <SelectValue placeholder="Select variant" />
                    </SelectTrigger>
                    <SelectContent side="bottom" align="start">
                      {matchingSnippets.map((s) => (
                        <SelectItem
                          key={s._id}
                          value={s._id}
                          className="text-xs"
                        >
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
            {blocks &&
              groupBlocks.length > 0 &&
              expandedGroups.has(group.slug) && (
                <div className="mt-1 flex flex-col gap-1.5 rounded-md border border-border/40 bg-muted/30 p-2">
                  {groupBlocks.map((b) => {
                    const overrideId = blockSnippetSelections?.[b.vbId] ?? "";
                    const overrideOpts = blockOverrideSnippets?.(b) ?? [];
                    return (
                      <div key={b.vbId} className="flex flex-col gap-0.5">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[11px] font-medium text-primary/70">
                            {b.slotLabel ?? "Variant block"}
                          </span>
                          {overrideOpts.length > 0 && onBlockSnippetChange && (
                            <Select
                              value={overrideId || undefined}
                              onValueChange={(val) =>
                                onBlockSnippetChange(b.vbId, val)
                              }
                            >
                              <SelectTrigger className="h-auto w-auto gap-1.5 rounded border border-border bg-background px-2 py-0.5 text-[11px] text-primary/70 transition hover:border-indigo-300">
                                <SelectValue placeholder="Override variant" />
                              </SelectTrigger>
                              <SelectContent side="bottom" align="start">
                                {overrideOpts.map((s) => (
                                  <SelectItem
                                    key={s._id}
                                    value={s._id}
                                    className="text-xs"
                                  >
                                    {s.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                        {b.previewHtml && (
                          <div
                            className="prose-letter snippet-preview text-[11px] leading-snug text-primary/70 [&_p]:m-0"
                            // previewHtml comes from the same render pipeline used
                            // for the document body — field placeholders are
                            // rendered as labelled chips (prose-letter/snippet-preview
                            // CSS styles + colours them). The snippet HTML itself is
                            // firm-authored and stored unsanitised, so sanitise at the
                            // render boundary (data-* chip attrs survive DOMPurify).
                            // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- DOMPurify.sanitize applied inline
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(b.previewHtml),
                            }}
                          />
                        )}
                        {overrideId && onBlockSnippetClear && (
                          <button
                            type="button"
                            onClick={() => onBlockSnippetClear(b.vbId)}
                            className="self-start text-[10px] font-medium text-red-500 hover:text-red-700 hover:underline"
                          >
                            Overrides default — reset
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            {docOverride && (
              <button
                type="button"
                onClick={() => onClear?.(group.slug)}
                className={cn(
                  "self-start font-medium text-red-500 transition-colors hover:text-red-700 hover:underline",
                  dense ? "text-[10px]" : "text-[11px]",
                )}
              >
                {isBound
                  ? "Overrides automatic selection — reset"
                  : `Overrides job default: ${jobDefaultLabel ?? "none selected"} — reset`}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
